import {
  type AdProductSelectionConnected,
  AdProductSelectionConnectedCart,
  GetAdProductsForAdEditingDocument,
} from '@seek/hirer-ad-products-selection';
import { type ComponentProps, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useSelector, shallowEqual } from 'react-redux';

import {
  SHOPPING_CART_KEY_EDIT_FLOW,
  SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY_EDIT_FLOW,
} from 'src/app/shared/constants/shoppingCart';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { log } from 'src/app/utils/logging';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { useHubbleTrackEventHandler } from 'src/app/v1/views/hooks/useHubbleTrackEventHandler';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import { AdSelectionErrorPage } from '../AdSelectionErrorPage';

const purchaseContextUseCase = 'AddonsOnly';

export const AdSelectionWrapperEditFlow = () => {
  const {
    advertiserId,
    advertiserNation,
    draftId,
    jobLocationCountryCode,
    jobLocationId,
    jobTitle,
    subClassificationId,
  } = useSelector(
    (state: AppState) => ({
      advertiserId: state.session.seekAdvertiserId,
      advertiserNation: state.session.hirerAccount.nation,
      draftId: state.session.draftId,
      jobLocationCountryCode: state.session.jobLocationCountry,
      jobLocationId: state.formData.jobLocationId,
      jobTitle: state.formData.jobTitle,
      subClassificationId: state.session.seekSubClassificationId,
    }),
    shallowEqual,
  );

  const { getLocale } = useConfig();
  const locale = getLocale();
  const { sendHubbleTrackEvent } = useHubbleTrackEventHandler();

  const { productSetId: shoppingCartProductSetId, cartState } =
    useShoppingCart();

  const adProductErrorHandler = useCallback(
    async (error: Error) => {
      log.error(
        'UpsellWidget | adProductErrorHandler',
        {
          error,
          advertiserId,
          advertiserNation,
          jobAdDetails: {
            locationId: jobLocationId.toString(),
            roleTitle: jobTitle,
            subClassificationId,
          },
          draftId,
        },
        true,
      );
    },
    [
      advertiserId,
      advertiserNation,
      draftId,
      jobLocationId,
      jobTitle,
      subClassificationId,
    ],
  );

  const onTrackingCallback = useCallback((eventData: unknown) => {
    const adSelectionEventData = eventData as TrackingEvent;
    getTrackingAdapter.sendLinkEvent(adSelectionEventData);
  }, []);

  const getAdProductSelectionConnectedProps = (
    productSetId: string,
  ): ComponentProps<typeof AdProductSelectionConnected> => ({
    errorComponent: <AdSelectionErrorPage />,
    context: {
      hirer: { id: advertiserId },
      products: { productSetId },
      draft: draftId ? { id: draftId } : undefined,
      job: {
        location: {
          id: jobLocationId,
          country: { code: jobLocationCountryCode?.toUpperCase() },
        },
      },
      purchaseContextUseCase,
    },
    layout: {
      hideDisclaimer: true,
    },
    onError: adProductErrorHandler,
    onTrackingEvent: (payload) => {
      if (payload.tracking) {
        onTrackingCallback(payload.tracking);
      }
      if (payload.hubble) {
        sendHubbleTrackEvent(
          payload.hubble.eventName,
          payload.hubble.eventData,
        );
      }
    },
    selectedProductIds: cartState?.items?.map((item) => item.product.id) ?? [],
  });

  const getAdProductsForEditDocumentConstructVariables = useCallback(
    (productSetIdForInput: string) => ({
      input: {
        productSetId: productSetIdForInput,
        locale,
        purchaseContextUseCase,
      },
    }),
    [locale],
  );

  const shoppingCart: ComponentProps<
    typeof AdProductSelectionConnectedCart
  >['shoppingCart'] = {
    getAdProductsDocument: GetAdProductsForAdEditingDocument,
    getAdProductsDocumentConstructVariables:
      getAdProductsForEditDocumentConstructVariables,
    key: SHOPPING_CART_KEY_EDIT_FLOW,
    subscriptionResponseKey: SHOPPING_CART_SUBSCRIPTION_RESPONSE_KEY_EDIT_FLOW,
  };

  if (!shoppingCartProductSetId) {
    return null;
  }

  return (
    <ErrorBoundary fallback={<AdSelectionErrorPage />}>
      <AdProductSelectionConnectedCart
        {...getAdProductSelectionConnectedProps(shoppingCartProductSetId)}
        onProductSelect={undefined}
        shoppingCart={shoppingCart}
      />
    </ErrorBoundary>
  );
};
